import React, { FC, useEffect, useState } from "react";
import {
    DropdownAction,
    Project,
    ProjectMetaData,
    ProjectsData,
    ProjectSettings,
    ProjectVariables,
    UpdateProjectData,
    UserProject,
    UserProjectData,
    UserProjectVariables
} from "../../models/types";
import {
    Card,
    Col,
    Collapse,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import { ProjectSettingsModal } from "../modals/ProjectSettingsModal";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { UPDATE_PROJECT, UPDATE_USER_PROJECT } from "../../graphql/mutations";
import {
    GET_PROJECT_META_DATA,
    GET_PROJECTS_BY_PRODUCT,
    GET_USER_PRODUCTS
} from "../../graphql/queries";
import { ActionDropdown } from "../dropdowns/ActionDropdown";
import { Project as ProjectView } from "../views/Project";
import { useLocalAnalyticsState, useLocalState } from "../../graphql/hooks";
import { AnalyticsFunnelSelectionToggle } from "../buttons/analytics/AnalyticsFunnelSelectionToggle";
import {
    saveCollapseState,
    updateCollapseState,
    updateCollapseTiersInCache,
    updateProjectFavorites
} from "../../common/Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faStar } from "@fortawesome/free-solid-svg-icons";
import { SimpleDate } from "./SimpleDate";
import { DailyCTR } from "./analytics/DailyCTR";
import { DailyImpressions } from "./analytics/DailyImpressions";
import { UserQueryCTR } from "./analytics/UserQueryCTR";
import { UserQueryImpressions } from "./analytics/UserQueryImpressions";
import { StatusBadges } from "./StatusBadges";
import cx from "classnames";

interface Props {
    item: Project;
    userProject?: UserProject;
    productId: number;
    newItemText?: string;
    onCreate?: (variables?: any) => void;
    onRemove: () => void;
}

const ProjectCardItemImplementation: FC<Props> = ({
    item,
    productId,
    userProject,
    onRemove
}) => {
    const client = useApolloClient();
    const state = useLocalState();
    const { analyticsEnabled, analyticsFunnelSelectionMode } =
        useLocalAnalyticsState();
    const [loaded, updateLoaded] = useState(false);
    const [showChildren, updateShowChildren] = useState(
        state.projectTier && state.projectTier.has(Number(item.id))
    );
    const [showSettingsModal, updateShowSettingsModal] = useState(false);

    const { data: { projectMetaData } = {} } = useQuery<
        ProjectMetaData,
        ProjectVariables
    >(GET_PROJECT_META_DATA, {
        variables: { projectId: Number(item?.id) },
        fetchPolicy: "network-only"
    });

    const [updateProjectMutation] = useMutation<
        UpdateProjectData,
        ProjectSettings
    >(UPDATE_PROJECT);

    const [updateUserProject] = useMutation<
        UserProjectData,
        UserProjectVariables
    >(UPDATE_USER_PROJECT);

    useEffect(() => {
        const updatedSet = updateCollapseState(
            state.projectTier,
            Number(item.id),
            showChildren
        );
        saveCollapseState(1, updatedSet);
        updateCollapseTiersInCache(client, undefined, updatedSet);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showChildren]);

    const updateProject = async (setting: ProjectSettings) => {
        try {
            await updateProjectMutation({
                variables: setting,
                update: (cache, { data }) => {
                    const currentProjectsData = cache.readQuery<
                        ProjectsData,
                        ProjectVariables
                    >({
                        query: GET_PROJECTS_BY_PRODUCT,
                        variables: { productId: productId }
                    });
                    const currentProjects = currentProjectsData?.projects;
                    if (currentProjects && data) {
                        const newProjects = currentProjects.map(project =>
                            project.id === data.updateProject.id
                                ? data.updateProject
                                : project
                        );
                        cache.writeQuery<ProjectsData, ProjectVariables>({
                            query: GET_PROJECTS_BY_PRODUCT,
                            data: { projects: newProjects }
                        });
                    }
                }
            });
            updateShowSettingsModal(false);
        } catch (error) {
            console.log("[DEBUG] updateProject error ", error);
        }
    };

    if (!item) {
        return null;
    }

    const actions: DropdownAction[] = [
        {
            header: "Project Settings",
            variant: "inverse",
            callback: () => updateShowSettingsModal(true)
        },
        {
            header: "Remove Project",
            variant: "danger",
            callback: onRemove
        }
    ];

    const favoriteClass = !!userProject ? "text-warning" : "text-inverse-25";

    const handleFavoriteUpdate = async () => {
        const result = await updateUserProject({
            variables: {
                input: {
                    projectId: Number(item.id),
                    favorite: !userProject
                }
            },
            refetchQueries: [
                {
                    query: GET_USER_PRODUCTS
                }
            ]
        });
        updateProjectFavorites(client, result.data?.userProject, item.id);
    };

    return (
        <Card className="border-0">
            <Card.Header
                className="text-inverse custom-card-project pb-1"
                onClick={e => {
                    if (e.defaultPrevented) {
                        return;
                    }
                    updateShowChildren(!showChildren);
                }}
            >
                <Row className="d-flex align-items-center no-wrap medium-font">
                    <Col
                        className={cx("text-start ps-2", {
                            "col-5": analyticsEnabled,
                            "col-8 col-xl-7": !analyticsEnabled
                        })}
                    >
                        <OverlayTrigger
                            key={Date.now()}
                            delay={{
                                show: 550,
                                hide: 100
                            }}
                            overlay={<Tooltip>Toggle Favorite</Tooltip>}
                        >
                            <FontAwesomeIcon
                                icon={faStar}
                                size="xs"
                                className={`me-1 ${favoriteClass}`}
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleFavoriteUpdate();
                                }}
                            />
                        </OverlayTrigger>
                        <span className="ms-2">{item?.name}</span>
                        <FontAwesomeIcon
                            className={`icon-chevron ms-2 ${
                                showChildren ? "rotate-180" : ""
                            }`}
                            icon={faChevronDown}
                        />
                    </Col>
                    <Col className="col-1 d-flex text-nowrap xs-font justify-content-center">
                        <SimpleDate dateUpdated={item.dateUpdated} />
                    </Col>
                    <Col
                        className={cx("ps-3 d-flex justify-content-center", {
                            "col-1": analyticsEnabled,
                            "col-2 col-xl-3": !analyticsEnabled
                        })}
                    >
                        {projectMetaData ? (
                            <StatusBadges metaData={projectMetaData} />
                        ) : null}
                    </Col>
                    {analyticsEnabled ? (
                        <>
                            <Col className="col-1 text-start ps-2 pe-0">
                                <DailyImpressions
                                    impressions={item.impressions}
                                />
                            </Col>
                            <Col className="col-1 text-start pe-0">
                                <DailyCTR
                                    gameplay={item.gameplay}
                                    endScreen={item.endScreen}
                                    impressions={item.impressions}
                                />
                            </Col>
                            <Col className="col-1 text-start ms-1 pe-0">
                                <UserQueryImpressions
                                    impressions={item.userImpressions}
                                />
                            </Col>
                            <Col className="col-1 text-start pe-0">
                                <UserQueryCTR
                                    gameplay={item.userGameplay}
                                    endScreen={item.userEndScreen}
                                    impressions={item.userImpressions}
                                />
                            </Col>
                        </>
                    ) : null}
                    <Col className="d-flex col-1 justify-content-end p-0">
                        {analyticsEnabled && analyticsFunnelSelectionMode ? (
                            <AnalyticsFunnelSelectionToggle
                                reference="project"
                                project={{
                                    ...item,
                                    selected: true,
                                    comparison: `project-${item.id}`
                                }}
                                className="custom-funnel-button"
                            />
                        ) : null}
                        {actions ? (
                            <ActionDropdown
                                id={`project-${item.id}`}
                                variant="success"
                                actions={actions}
                            />
                        ) : null}
                    </Col>
                </Row>
            </Card.Header>
            <Collapse in={showChildren && loaded}>
                <div>
                    <Card.Body className="custom-card-body pe-2 fullSizeChild">
                        <ProjectView
                            project={item}
                            showChildren={showChildren}
                            updateLoaded={updateLoaded}
                        />
                    </Card.Body>
                </div>
            </Collapse>
            <ProjectSettingsModal
                project={item}
                show={showSettingsModal}
                onClose={() => updateShowSettingsModal(false)}
                onSave={updateProject}
            />
        </Card>
    );
};

export const ProjectCardItem = React.memo(ProjectCardItemImplementation);
